import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { type MenuItem } from '@/types/common';
import menuApp from '@/pages/menuApp';
import PrivateRoute from '@/routes/PrivateRoute';
import useApi from '@/hooks/useApi';
import useRedirectToLogin from '@/hooks/useRedirectToLogin';
import Headline from '@/components/Headline';
import Sidebar from '@/components/Sidebar';
import MobileTopbar from '@/components/MobileTopbar';
import MobileDock from '@/components/MobileDock';
import MobileMoreSheet from '@/components/MobileMoreSheet';
import SuspenseLoader from '@/components/SuspenseLoader';
import { ApiCacheProvider } from '@/pages/ApiCache.context';
import { AppProvider } from '@/pages/App.context';
import useAuth, { UserProvider } from '@/components/UserContext';
import styles from './AppLayout.module.scss';

interface AppShellProps {
  menuItems: MenuItem[];
}

// Routes that take over the screen on mobile — the dock + topbar would compete
// with their own sticky footers or full-bleed content, so we hide them here.
const MOBILE_FULL_SCREEN_ROUTES = ['/account/subscription/checkout'];

const isMobileFullScreen = (pathname: string): boolean => {
  return MOBILE_FULL_SCREEN_ROUTES.some((route) => pathname.startsWith(route));
};

const AppShell = ({ menuItems }: AppShellProps) => {
  const { hasAccessTo } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const mobileFullScreen = isMobileFullScreen(location.pathname);

  // C2PA POC is server-gated (local/test/staging only) — the flag decides
  // whether the Content Credentials entry exists at all.
  const [getC2paStatus, { response: c2paStatus }] = useApi('c2pa.status');
  useEffect(() => {
    void getC2paStatus();
  }, [getC2paStatus]);
  const c2paEnabled = c2paStatus?.data.enabled === true;

  const checkMenuItemPermission = (item: MenuItem) => {
    if (item.href === '/content-credentials' && !c2paEnabled) {
      return false;
    }
    return hasAccessTo(item.href);
  };

  // Recursively filter submenu children so admin-only entries (members /
  // subscription / invoicing under "Profile") don't show for team members.
  const filterMenuTree = (items: MenuItem[]): MenuItem[] =>
    items
      .filter(checkMenuItemPermission)
      .map((item) =>
        item.submenuItems
          ? { ...item, submenuItems: filterMenuTree(item.submenuItems) }
          : item
      );

  const accessibleItems = filterMenuTree(menuItems);

  const primaryItems = accessibleItems.filter((item) => item.primaryMobile);

  return (
    <>
      <Sidebar menuItems={accessibleItems} />
      {!mobileFullScreen && <MobileTopbar />}
      <section className={styles.content}>
        <div className={styles.pageContainer}>
          <Headline />
          <Outlet />
        </div>
      </section>
      {!mobileFullScreen && (
        <MobileDock
          primaryItems={primaryItems}
          onOpenMore={() => setMoreOpen(true)}
        />
      )}
      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        menuItems={accessibleItems}
      />
    </>
  );
};

const IndexLayout = () => {
  const redirectToLogin = useRedirectToLogin();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      redirectToLogin(true);
    }
  }, [token, redirectToLogin]);

  if (!token) {
    return <SuspenseLoader />;
  }

  /*
   * Please do not add any Suspense here as this feels somehow broken!
   * */
  return (
    <PrivateRoute>
      <ApiCacheProvider token={token}>
        <AppProvider>
          <main className={styles.main}>
            <UserProvider>
              <AppShell menuItems={menuApp} />
            </UserProvider>
          </main>
        </AppProvider>
      </ApiCacheProvider>
    </PrivateRoute>
  );
};

export default IndexLayout;
